import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, QrCode, Printer, Share2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventQRCodeManager({ eventId, event }) {
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQRCode();
  }, [eventId]);

  const fetchQRCode = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/events/${eventId}/preregistration-qr-base64`);
      setQrData(response.data);
    } catch (error) {
      console.error('Error fetching QR code:', error);
      toast.error('Errore nel caricamento QR code');
    } finally {
      setLoading(false);
    }
  };

  const downloadQR = async () => {
    try {
      const response = await axios.get(`${API_URL}/events/${eventId}/preregistration-qr`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `QR_${event.title.replace(/\s+/g, '_')}.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('QR Code scaricato!');
    } catch (error) {
      toast.error('Errore nel download');
    }
  };

  const printQR = () => {
    if (!qrData) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>QR Code - ${event.title}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            text-align: center;
            padding: 40px;
          }
          .container {
            max-width: 600px;
            margin: 0 auto;
            border: 2px solid #333;
            padding: 30px;
          }
          h1 {
            color: #333;
            margin-bottom: 10px;
          }
          .subtitle {
            color: #666;
            font-size: 18px;
            margin-bottom: 30px;
          }
          img {
            max-width: 400px;
            height: auto;
            margin: 20px 0;
          }
          .url {
            font-size: 14px;
            color: #666;
            word-break: break-all;
            margin-top: 20px;
          }
          .instructions {
            margin-top: 30px;
            padding: 20px;
            background: #f0f0f0;
            border-radius: 8px;
          }
          @media print {
            body { padding: 0; }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <h1>🎉 ${event.title}</h1>
          <p class="subtitle">Scansiona per Pre-Registrarti</p>
          <img src="${qrData.qr_code_base64}" alt="QR Code">
          <p class="url">${qrData.event_url}</p>
          <div class="instructions">
            <p><strong>📱 Come Usare:</strong></p>
            <p>1. Inquadra il QR code con la fotocamera</p>
            <p>2. Apri il link che appare</p>
            <p>3. Acquista il tuo biglietto online</p>
          </div>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const shareQR = async () => {
    if (!qrData) return;
    
    // Try native share if available
    if (navigator.share) {
      try {
        await navigator.share({
          title: event.title,
          text: `Pre-registrati all'evento: ${event.title}`,
          url: qrData.event_url
        });
        toast.success('Condiviso!');
      } catch (error) {
        if (error.name !== 'AbortError') {
          copyToClipboard();
        }
      }
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(qrData.event_url);
    toast.success('Link copiato negli appunti!');
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-gray-600">Generazione QR Code...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <QrCode className="w-5 h-5" />
          QR Code Pre-Registrazione
        </CardTitle>
        <p className="text-sm text-gray-600">
          Usa questo QR code su locandine, poster, social media per far pre-registrare i partecipanti
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* QR Code Display */}
        <div className="flex justify-center">
          <div className="p-6 bg-white border-2 border-purple-200 rounded-lg">
            {qrData && (
              <img 
                src={qrData.qr_code_base64} 
                alt="QR Code Pre-Registrazione"
                className="w-64 h-64"
              />
            )}
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-900 font-semibold mb-2">📱 Come Funziona:</p>
          <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
            <li>Scarica o stampa il QR code</li>
            <li>Inseriscilo su locandine, poster, social media</li>
            <li>I partecipanti lo scanneranno con lo smartphone</li>
            <li>Verranno portati alla pagina di acquisto biglietti</li>
            <li>Registrazione e pagamento immediati</li>
          </ol>
        </div>

        {/* Event URL */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-600 mb-1">Link Evento:</p>
          <p className="text-sm font-mono text-gray-800 break-all">{qrData?.event_url}</p>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-3">
          <Button
            onClick={downloadQR}
            className="bg-green-600 hover:bg-green-700 gap-2"
          >
            <Download className="w-4 h-4" />
            Scarica PNG
          </Button>

          <Button
            onClick={printQR}
            variant="outline"
            className="gap-2"
          >
            <Printer className="w-4 h-4" />
            Stampa
          </Button>

          <Button
            onClick={shareQR}
            variant="outline"
            className="col-span-2 gap-2"
          >
            <Share2 className="w-4 h-4" />
            Condividi Link
          </Button>
        </div>

        {/* Usage Tips */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <p className="text-sm font-semibold text-purple-900 mb-2">💡 Suggerimenti d'Uso:</p>
          <ul className="text-sm text-purple-800 space-y-1">
            <li>✅ Stampa su A4 per poster da appendere</li>
            <li>✅ Inserisci nelle stories Instagram/Facebook</li>
            <li>✅ Aggiungi a newsletter e email marketing</li>
            <li>✅ Metti su biglietti da visita per eventi ricorrenti</li>
            <li>✅ Proietta su schermo durante eventi precedenti</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
